import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const TOOLTIP_STYLE = {
  background: "#050505",
  border: "1px solid #222",
  borderRadius: 0,
  fontFamily: "IBM Plex Mono",
  fontSize: 12,
};
const tooltipFormatter = (v, n) => [`${v}%`, n];

export default function Tokenomics() {
  const [t, setT] = useState(null);

  useEffect(() => {
    api.get("/tokenomics").then((r) => setT(r.data)).catch(() => {});
  }, []);

  if (!t) return null;

  return (
    <section
      id="tokenomics"
      className="border-t border-[#3a3a3a] relative"
      data-testid="section-tokenomics"
    >
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            "url(https://static.prod-images.emergentagent.com/jobs/3681aa5c-86db-4b26-b358-26b9f5039b01/images/b11c866f756cb47bbc11d3669f879e984e3342a72aa7f03ec66609c6e109370b.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-6 mb-12">
          <div className="col-span-12 lg:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
              03 // Tokenomics
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tighter">
              Supply: <span className="text-[#cfff04]">1,000,000,000</span>
              <br />
              Math that doesn't lie.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3">
          {/* Donut */}
          <div className="col-span-12 lg:col-span-5 border border-[#3a3a3a] bg-[#121212] p-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#d0d0d0] mb-4">
              Allocation
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} initialDimension={{ width: 320, height: 240 }}>
                <PieChart>
                  <Pie
                    data={t.allocation}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                    stroke="#050505"
                    strokeWidth={2}
                  >
                    {t.allocation.map((a) => (
                      <Cell key={a.label} fill={a.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={tooltipFormatter} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {t.allocation.map((a) => (
                <div key={a.label} className="flex items-center gap-2">
                  <span className="w-2 h-2" style={{ background: a.color }} />
                  <span className="font-mono text-[11px] uppercase tracking-wider text-[#e2e2e2]">
                    {a.label} {a.value}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* breakdown table */}
          <div className="col-span-12 lg:col-span-7 border border-[#3a3a3a] bg-[#121212]">
            <div className="grid grid-cols-12 border-b border-[#3a3a3a] px-6 py-3 font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
              <div className="col-span-5">Bucket</div>
              <div className="col-span-2 text-right">%</div>
              <div className="col-span-5 text-right">Vesting / Use</div>
            </div>
            {t.allocation.map((a) => (
              <div
                key={a.label}
                className="grid grid-cols-12 px-6 py-4 border-b border-[#161616] hover:bg-[#1a1a1a] transition-colors items-center"
                data-testid={`tokenomic-row-${a.label.replace(/\s/g, "-").toLowerCase()}`}
              >
                <div className="col-span-5 flex items-center gap-3">
                  <span className="w-3 h-3" style={{ background: a.color }} />
                  <span className="font-display font-bold uppercase tracking-tight text-sm">
                    {a.label}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono font-bold text-base">
                  {a.value}%
                </div>
                <div className="col-span-5 text-right font-mono text-xs text-[#e2e2e2]">
                  {a.note}
                </div>
              </div>
            ))}
            {/* totals */}
            <div className="grid grid-cols-3 px-6 py-5 bg-[#050505]">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                  Total Supply
                </div>
                <div className="font-display text-xl font-bold mt-1">
                  {t.total_supply.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                  Decimals
                </div>
                <div className="font-display text-xl font-bold mt-1">{t.decimals}</div>
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                  Ticker
                </div>
                <div className="font-display text-xl font-bold mt-1 text-[#cfff04]">
                  ${t.symbol}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
